import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { consumePostLoginPath, setStoredAuth } from "../auth";

function decodeBase64Url(input: string): Uint8Array {
  const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const raw = atob(padded);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function toBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function encodeUtf8(value: string): Uint8Array {
  return new TextEncoder().encode(value);
}

function parseJwtClaims(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload.padEnd(Math.ceil(payload.length / 4) * 4, "=");
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [methods, setMethods] = useState<string[]>([]);
  const [code, setCode] = useState("");
  const [method, setMethod] = useState<"totp" | "recovery_code">("totp");
  const [totpEnrollChallengeId, setTotpEnrollChallengeId] = useState<string | null>(null);
  const [totpEnrollSecret, setTotpEnrollSecret] = useState<string | null>(null);
  const [totpEnrollUri, setTotpEnrollUri] = useState<string | null>(null);
  const [totpEnrollQrDataUrl, setTotpEnrollQrDataUrl] = useState<string | null>(null);
  const [totpEnrollCode, setTotpEnrollCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = useMemo(
    () => (location.state as { from?: string })?.from ?? consumePostLoginPath() ?? "/",
    [location.state],
  );

  const challengeFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      challengeId: params.get("loginChallenge"),
      methods: params.get("methods")?.split(",").filter(Boolean) ?? [],
    };
  }, [location.search]);

  useEffect(() => {
    if (challengeFromQuery.challengeId) {
      setChallengeId(challengeFromQuery.challengeId);
      setMethods(challengeFromQuery.methods.length ? challengeFromQuery.methods : ["totp"]);
    }
  }, [challengeFromQuery]);

  useEffect(() => {
    let active = true;
    if (!totpEnrollUri) {
      setTotpEnrollQrDataUrl(null);
      return;
    }

    QRCode.toDataURL(totpEnrollUri, {
      width: 192,
      margin: 1,
    })
      .then((value) => {
        if (active) setTotpEnrollQrDataUrl(value);
      })
      .catch(() => {
        if (active) setTotpEnrollQrDataUrl(null);
      });

    return () => {
      active = false;
    };
  }, [totpEnrollUri]);

  async function beginPassword(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login/password/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier, password, audience: "hank" }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        challengeId?: string;
        methods?: string[];
        token?: string;
        accessToken?: string;
        refreshToken?: string;
        claims?: {
          sid?: string;
          sub?: string;
          email?: string;
          role?: string;
          tenant_id?: string;
          aal?: number;
          stepup_until?: number;
        };
      };
      if (!res.ok) {
        throw new Error(data.error ?? "Unable to start login");
      }

      const token = data.token ?? data.accessToken;
      if (token) {
        const parsedClaims = parseJwtClaims(token);
        const claims = data.claims ?? {};
        const sid = claims.sid ?? (typeof parsedClaims?.sid === "string" ? parsedClaims.sid : null);
        const sub = claims.sub ?? (typeof parsedClaims?.sub === "string" ? parsedClaims.sub : null);
        const emailClaim =
          claims.email ?? (typeof parsedClaims?.email === "string" ? parsedClaims.email : null);
        const role =
          claims.role ?? (typeof parsedClaims?.role === "string" ? parsedClaims.role : null);
        const tenantId =
          claims.tenant_id ??
          (typeof parsedClaims?.tenant_id === "string" ? parsedClaims.tenant_id : null);
        const aal =
          typeof claims.aal === "number"
            ? claims.aal
            : typeof parsedClaims?.aal === "number"
              ? parsedClaims.aal
              : 1;
        const stepupUntil =
          typeof claims.stepup_until === "number"
            ? claims.stepup_until
            : typeof parsedClaims?.stepup_until === "number"
              ? parsedClaims.stepup_until
              : null;

        setStoredAuth({
          token,
          refreshToken: data.refreshToken ?? null,
          sid,
          sub,
          email: emailClaim ?? identifier,
          role,
          tenantId,
          aal,
          stepupUntil,
        });
        navigate(from, { replace: true });
        return;
      }

      const challenge = typeof data.challengeId === "string" ? data.challengeId : null;
      const availableMethods = Array.isArray(data.methods)
        ? data.methods.filter((value): value is string => typeof value === "string")
        : [];
      if (!challenge) {
        throw new Error("Login response missing challenge metadata");
      }

      setChallengeId(challenge);
      setMethods(availableMethods);
      if (availableMethods.includes("recovery_code") && !availableMethods.includes("totp")) {
        setMethod("recovery_code");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start login");
    } finally {
      setLoading(false);
    }
  }

  async function completeTotp(e: React.FormEvent) {
    e.preventDefault();
    if (!challengeId) return;
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/totp/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId, code, method }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as { error?: string }).error ?? "Verification failed");
      }

      const payload = data as {
        token: string;
        refreshToken?: string;
        claims: {
          sid: string;
          sub: string;
          email: string;
          role: string;
          tenant_id?: string;
          aal: number;
          stepup_until?: number;
        };
      };

      setStoredAuth({
        token: payload.token,
        refreshToken: payload.refreshToken ?? null,
        sid: payload.claims.sid,
        sub: payload.claims.sub,
        email: payload.claims.email,
        role: payload.claims.role,
        tenantId: payload.claims.tenant_id ?? null,
        aal: payload.claims.aal,
        stepupUntil: payload.claims.stepup_until ?? null,
      });

      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setLoading(false);
    }
  }

  async function startTotpEnrollment() {
    if (!challengeId) return;
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/totp/enroll/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId, label: "Agent Me authenticator" }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        challengeId?: string;
        secret?: string;
        otpauthUri?: string;
        error?: string;
      };
      if (!res.ok || !data.challengeId || !data.secret) {
        throw new Error(data.error ?? "Unable to start authenticator setup");
      }
      setTotpEnrollChallengeId(data.challengeId);
      setTotpEnrollSecret(data.secret);
      setTotpEnrollUri(data.otpauthUri ?? null);
      setTotpEnrollCode("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start authenticator setup");
    } finally {
      setLoading(false);
    }
  }

  async function verifyTotpEnrollment(e: React.FormEvent) {
    e.preventDefault();
    if (!totpEnrollChallengeId || !totpEnrollCode) return;
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/totp/enroll/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challengeId: totpEnrollChallengeId,
          code: totpEnrollCode.trim(),
        }),
      });
      const payload = (await res.json().catch(() => ({}))) as {
        token?: string;
        refreshToken?: string;
        claims?: {
          sid: string;
          sub: string;
          email: string;
          role: string;
          tenant_id?: string;
          aal: number;
          stepup_until?: number;
        };
        error?: string;
      };

      if (!res.ok || !payload.token || !payload.claims) {
        throw new Error(payload.error ?? "Authenticator setup verification failed");
      }

      setStoredAuth({
        token: payload.token,
        refreshToken: payload.refreshToken ?? null,
        sid: payload.claims.sid,
        sub: payload.claims.sub,
        email: payload.claims.email,
        role: payload.claims.role,
        tenantId: payload.claims.tenant_id ?? null,
        aal: payload.claims.aal,
        stepupUntil: payload.claims.stepup_until ?? null,
      });

      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authenticator setup verification failed");
    } finally {
      setLoading(false);
    }
  }

  async function completePasskey() {
    if (!challengeId) return;
    setError("");
    setLoading(true);

    try {
      if (!("PublicKeyCredential" in window)) {
        throw new Error("Passkeys are not available in this browser");
      }

      const optionsRes = await fetch("/api/auth/passkeys/auth/options", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId }),
      });
      const optionsData = (await optionsRes.json().catch(() => ({}))) as {
        challengeId?: string;
        publicKeyOptions?: {
          challenge: string;
          rpId?: string;
          timeout?: number;
          allowCredentials: Array<{ id: string; type: "public-key" }>;
          userVerification?: "required" | "preferred" | "discouraged";
        };
        error?: string;
      };
      if (!optionsRes.ok || !optionsData.challengeId || !optionsData.publicKeyOptions) {
        throw new Error(optionsData.error ?? "Unable to get passkey options");
      }

      const cred = (await navigator.credentials.get({
        publicKey: {
          challenge: new TextEncoder().encode(optionsData.publicKeyOptions.challenge),
          rpId: optionsData.publicKeyOptions.rpId,
          timeout: optionsData.publicKeyOptions.timeout,
          userVerification: optionsData.publicKeyOptions.userVerification,
          allowCredentials: optionsData.publicKeyOptions.allowCredentials.map((entry) => ({
            id: decodeBase64Url(entry.id),
            type: entry.type,
          })),
        },
      })) as PublicKeyCredential | null;

      if (!cred) throw new Error("Passkey prompt cancelled");

      const verifyRes = await fetch("/api/auth/passkeys/auth/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challengeId: optionsData.challengeId,
          credentialId: toBase64Url(cred.rawId),
        }),
      });
      const payload = (await verifyRes.json().catch(() => ({}))) as {
        token?: string;
        refreshToken?: string;
        claims?: {
          sid: string;
          sub: string;
          email: string;
          role: string;
          tenant_id?: string;
          aal: number;
          stepup_until?: number;
        };
        error?: string;
      };

      if (!verifyRes.ok || !payload.token || !payload.claims) {
        throw new Error(payload.error ?? "Passkey verification failed");
      }

      setStoredAuth({
        token: payload.token,
        refreshToken: payload.refreshToken ?? null,
        sid: payload.claims.sid,
        sub: payload.claims.sub,
        email: payload.claims.email,
        role: payload.claims.role,
        tenantId: payload.claims.tenant_id ?? null,
        aal: payload.claims.aal,
        stepupUntil: payload.claims.stepup_until ?? null,
      });

      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Passkey sign in failed");
    } finally {
      setLoading(false);
    }
  }

  async function enrollPasskey() {
    if (!challengeId) return;
    setError("");
    setLoading(true);

    try {
      if (!("PublicKeyCredential" in window)) {
        throw new Error("Passkeys are not available in this browser");
      }

      const optionsRes = await fetch("/api/auth/passkeys/register/options", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ loginChallengeId: challengeId, label: "Agent Me passkey" }),
      });
      const optionsData = (await optionsRes.json().catch(() => ({}))) as {
        challengeId?: string;
        publicKeyOptions?: {
          challenge: string;
          rp: PublicKeyCredentialRpEntity;
          user: { id: string; name: string; displayName: string };
          timeout?: number;
          attestation?: AttestationConveyancePreference;
        };
        error?: string;
      };
      if (!optionsRes.ok || !optionsData.challengeId || !optionsData.publicKeyOptions) {
        throw new Error(optionsData.error ?? "Unable to get passkey enrollment options");
      }

      const cred = (await navigator.credentials.create({
        publicKey: {
          challenge: encodeUtf8(optionsData.publicKeyOptions.challenge),
          rp: optionsData.publicKeyOptions.rp,
          user: {
            id: encodeUtf8(optionsData.publicKeyOptions.user.id),
            name: optionsData.publicKeyOptions.user.name,
            displayName: optionsData.publicKeyOptions.user.displayName,
          },
          timeout: optionsData.publicKeyOptions.timeout,
          attestation: optionsData.publicKeyOptions.attestation ?? "none",
          pubKeyCredParams: [
            { alg: -7, type: "public-key" }, // ES256
            { alg: -257, type: "public-key" }, // RS256
          ],
        },
      })) as PublicKeyCredential | null;

      if (!cred) throw new Error("Passkey enrollment prompt cancelled");

      const verifyRes = await fetch("/api/auth/passkeys/register/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challengeId: optionsData.challengeId,
          credentialId: toBase64Url(cred.rawId),
        }),
      });
      const payload = (await verifyRes.json().catch(() => ({}))) as {
        token?: string;
        refreshToken?: string;
        claims?: {
          sid: string;
          sub: string;
          email: string;
          role: string;
          tenant_id?: string;
          aal: number;
          stepup_until?: number;
        };
        error?: string;
      };

      if (!verifyRes.ok || !payload.token || !payload.claims) {
        throw new Error(payload.error ?? "Passkey enrollment verification failed");
      }

      setStoredAuth({
        token: payload.token,
        refreshToken: payload.refreshToken ?? null,
        sid: payload.claims.sid,
        sub: payload.claims.sub,
        email: payload.claims.email,
        role: payload.claims.role,
        tenantId: payload.claims.tenant_id ?? null,
        aal: payload.claims.aal,
        stepupUntil: payload.claims.stepup_until ?? null,
      });

      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Passkey enrollment failed");
    } finally {
      setLoading(false);
    }
  }

  function startGoogleLogin() {
    const returnTo = encodeURIComponent("/login");
    window.location.href = `/api/auth/login/google/start?audience=hank&returnTo=${returnTo}`;
  }

  const supportsTotpVerification = methods.includes("totp") || methods.includes("recovery_code");
  const supportsTotpEnrollment = methods.includes("totp_enroll");

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--c-bg)",
        padding: "1rem",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--c-surface)",
          border: "1px solid var(--c-border)",
          borderRadius: 12,
          padding: "1.5rem",
        }}
      >
        <div style={{ marginBottom: "1rem", textAlign: "center" }}>
          <div
            style={{
              fontSize: 12,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--c-text-faint)",
              marginBottom: 8,
            }}
          >
            AgentMe
          </div>
          <h1 style={{ margin: 0, fontSize: "2rem", fontWeight: 600, color: "var(--c-text)" }}>
            Agent Me
          </h1>
          <p style={{ margin: "0.25rem 0 0", color: "var(--c-text-secondary)" }}>
            {challengeId ? "Complete secure sign in" : "Sign in to your agent"}
          </p>
        </div>

        {!challengeId ? (
          <>
            <form onSubmit={beginPassword}>
              <label
                htmlFor="login-identifier"
                style={{
                  display: "block",
                  fontSize: 12,
                  marginBottom: 6,
                  color: "var(--c-text-secondary)",
                }}
              >
                Email or username
              </label>
              <input
                id="login-identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@company.com"
                autoComplete="username"
                required
                style={{
                  width: "100%",
                  marginBottom: 12,
                  borderRadius: 8,
                  border: "1px solid var(--c-border)",
                  background: "var(--c-surface-2)",
                  color: "var(--c-text)",
                  padding: "0.6rem 0.7rem",
                }}
              />

              <label
                htmlFor="login-password"
                style={{
                  display: "block",
                  fontSize: 12,
                  marginBottom: 6,
                  color: "var(--c-text-secondary)",
                }}
              >
                Password
              </label>
              <input
                id="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
                type="password"
                required
                style={{
                  width: "100%",
                  marginBottom: 12,
                  borderRadius: 8,
                  border: "1px solid var(--c-border)",
                  background: "var(--c-surface-2)",
                  color: "var(--c-text)",
                  padding: "0.6rem 0.7rem",
                }}
              />

              <button
                type="submit"
                disabled={loading || !identifier || !password}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  border: "none",
                  padding: "0.65rem 0.75rem",
                  background: "var(--c-accent)",
                  color: "var(--c-accent-foreground)",
                  fontWeight: 600,
                }}
              >
                {loading ? "Checking credentials..." : "Continue"}
              </button>
            </form>

            <div
              style={{
                margin: "1rem 0",
                fontSize: 12,
                color: "var(--c-text-faint)",
                textAlign: "center",
              }}
            >
              or
            </div>

            <button
              type="button"
              onClick={startGoogleLogin}
              style={{
                width: "100%",
                borderRadius: 8,
                border: "1px solid var(--c-border)",
                padding: "0.65rem 0.75rem",
                background: "var(--c-surface-2)",
                color: "var(--c-text)",
                fontWeight: 500,
              }}
            >
              Continue with Google
            </button>

            <p
              style={{
                marginTop: 12,
                fontSize: 12,
                color: "var(--c-text-faint)",
                textAlign: "center",
              }}
            >
              Need an account? <Link to="/register">Create account</Link>
            </p>
          </>
        ) : (
          <>
            {methods.includes("passkey_enroll") && (
              <button
                type="button"
                onClick={enrollPasskey}
                disabled={loading}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  border: "none",
                  marginBottom: 8,
                  padding: "0.65rem 0.75rem",
                  background: "var(--c-accent)",
                  color: "var(--c-accent-foreground)",
                  fontWeight: 600,
                }}
              >
                {loading ? "Waiting for passkey..." : "Enroll passkey and continue"}
              </button>
            )}

            {methods.includes("passkey") && (
              <button
                type="button"
                onClick={completePasskey}
                disabled={loading}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  border: methods.includes("passkey_enroll") ? "1px solid var(--c-border)" : "none",
                  marginBottom: 12,
                  padding: "0.65rem 0.75rem",
                  background: methods.includes("passkey_enroll")
                    ? "var(--c-surface-2)"
                    : "var(--c-accent)",
                  color: methods.includes("passkey_enroll")
                    ? "var(--c-text)"
                    : "var(--c-accent-foreground)",
                  fontWeight: 600,
                }}
              >
                {loading ? "Waiting for passkey..." : "Use passkey"}
              </button>
            )}

            {supportsTotpVerification && (
              <form onSubmit={completeTotp}>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  {methods.includes("totp") && (
                    <button
                      type="button"
                      onClick={() => setMethod("totp")}
                      style={{
                        borderRadius: 999,
                        border: "1px solid var(--c-border)",
                        padding: "0.25rem 0.6rem",
                        background: method === "totp" ? "var(--c-surface-2)" : "var(--c-surface)",
                        color: "var(--c-text-secondary)",
                        fontSize: 12,
                      }}
                    >
                      TOTP
                    </button>
                  )}
                  {methods.includes("recovery_code") && (
                    <button
                      type="button"
                      onClick={() => setMethod("recovery_code")}
                      style={{
                        borderRadius: 999,
                        border: "1px solid var(--c-border)",
                        padding: "0.25rem 0.6rem",
                        background:
                          method === "recovery_code" ? "var(--c-surface-2)" : "var(--c-surface)",
                        color: "var(--c-text-secondary)",
                        fontSize: 12,
                      }}
                    >
                      Recovery
                    </button>
                  )}
                </div>

                <label
                  htmlFor="login-mfa-code"
                  style={{
                    display: "block",
                    fontSize: 12,
                    marginBottom: 6,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  {method === "totp" ? "Authenticator code" : "Recovery code"}
                </label>

                <input
                  id="login-mfa-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={method === "totp" ? "123456" : "ABCDE-12345"}
                  required
                  style={{
                    width: "100%",
                    marginBottom: 12,
                    borderRadius: 8,
                    border: "1px solid var(--c-border)",
                    background: "var(--c-surface-2)",
                    color: "var(--c-text)",
                    padding: "0.6rem 0.7rem",
                  }}
                />

                <button
                  type="submit"
                  disabled={loading || !code}
                  style={{
                    width: "100%",
                    borderRadius: 8,
                    border: "none",
                    padding: "0.65rem 0.75rem",
                    background: "var(--c-accent)",
                    color: "var(--c-accent-foreground)",
                    fontWeight: 600,
                  }}
                >
                  {loading ? "Verifying..." : "Verify and sign in"}
                </button>
              </form>
            )}

            {supportsTotpEnrollment && (
              <div style={{ marginTop: supportsTotpVerification ? 12 : 0 }}>
                {!totpEnrollChallengeId ? (
                  <button
                    type="button"
                    onClick={startTotpEnrollment}
                    disabled={loading}
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      border: "1px solid var(--c-border)",
                      padding: "0.65rem 0.75rem",
                      background: "var(--c-surface-2)",
                      color: "var(--c-text)",
                      fontWeight: 600,
                    }}
                  >
                    {loading ? "Preparing authenticator setup..." : "Set up authenticator app"}
                  </button>
                ) : (
                  <form onSubmit={verifyTotpEnrollment}>
                    <div
                      style={{
                        marginBottom: 8,
                        fontSize: 12,
                        color: "var(--c-text-secondary)",
                        lineHeight: 1.5,
                      }}
                    >
                      Scan the QR in your authenticator app (or use the secret), then enter the
                      6-digit code.
                    </div>
                    {totpEnrollQrDataUrl ? (
                      <div
                        style={{
                          marginBottom: 8,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={totpEnrollQrDataUrl}
                          alt="Authenticator setup QR code"
                          style={{
                            width: 192,
                            height: 192,
                            borderRadius: 10,
                            border: "1px solid var(--c-border)",
                            background: "#fff",
                            padding: 6,
                          }}
                        />
                      </div>
                    ) : null}
                    <div
                      style={{
                        marginBottom: 8,
                        padding: "0.6rem 0.7rem",
                        borderRadius: 8,
                        border: "1px solid var(--c-border)",
                        background: "var(--c-surface-2)",
                        color: "var(--c-text)",
                        fontFamily: "monospace",
                        fontSize: 12,
                        wordBreak: "break-all",
                      }}
                    >
                      {totpEnrollSecret}
                    </div>
                    {totpEnrollUri ? (
                      <div
                        style={{
                          marginBottom: 10,
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <a
                          href={totpEnrollUri}
                          style={{
                            fontSize: 12,
                            padding: "0.45rem 0.6rem",
                            borderRadius: 8,
                            border: "1px solid var(--c-border)",
                            background: "var(--c-surface-2)",
                            color: "var(--c-text)",
                            textDecoration: "none",
                          }}
                        >
                          Open authenticator app
                        </a>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard?.writeText(totpEnrollSecret ?? "")}
                          style={{
                            fontSize: 12,
                            padding: "0.45rem 0.6rem",
                            borderRadius: 8,
                            border: "1px solid var(--c-border)",
                            background: "var(--c-surface-2)",
                            color: "var(--c-text)",
                          }}
                        >
                          Copy secret
                        </button>
                      </div>
                    ) : null}
                    <label
                      htmlFor="login-totp-enroll-code"
                      style={{
                        display: "block",
                        fontSize: 12,
                        marginBottom: 6,
                        color: "var(--c-text-secondary)",
                      }}
                    >
                      Authenticator code
                    </label>
                    <input
                      id="login-totp-enroll-code"
                      value={totpEnrollCode}
                      onChange={(e) => setTotpEnrollCode(e.target.value)}
                      placeholder="123456"
                      required
                      style={{
                        width: "100%",
                        marginBottom: 12,
                        borderRadius: 8,
                        border: "1px solid var(--c-border)",
                        background: "var(--c-surface-2)",
                        color: "var(--c-text)",
                        padding: "0.6rem 0.7rem",
                      }}
                    />
                    <button
                      type="submit"
                      disabled={loading || !totpEnrollCode}
                      style={{
                        width: "100%",
                        borderRadius: 8,
                        border: "none",
                        padding: "0.65rem 0.75rem",
                        background: "var(--c-accent)",
                        color: "var(--c-accent-foreground)",
                        fontWeight: 600,
                      }}
                    >
                      {loading ? "Verifying setup..." : "Verify setup and sign in"}
                    </button>
                  </form>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                setChallengeId(null);
                setMethods([]);
                setCode("");
                setTotpEnrollChallengeId(null);
                setTotpEnrollSecret(null);
                setTotpEnrollUri(null);
                setTotpEnrollQrDataUrl(null);
                setTotpEnrollCode("");
                setError("");
              }}
              style={{
                width: "100%",
                borderRadius: 8,
                border: "1px solid var(--c-border)",
                marginTop: 10,
                padding: "0.55rem 0.75rem",
                background: "var(--c-surface-2)",
                color: "var(--c-text-secondary)",
                fontWeight: 500,
              }}
            >
              Back
            </button>
          </>
        )}

        {error && (
          <p style={{ color: "var(--c-danger)", fontSize: 12, marginTop: 10, textAlign: "center" }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
